"use client"

import * as React from "react"
import { Check, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { smartTextMatches } from "@shared/search/smartSearch"

export interface AutocompleteOption {
  value: string
  label: string
  icon?: React.ReactNode
  group?: string
  groupHeader?: boolean
  highlighted?: boolean
}

interface AutocompleteProps {
  options: AutocompleteOption[]
  value?: string
  selectedLabel?: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  notFoundText?: string
  className?: string
  disabled?: boolean
  onSearchChange?: (search: string) => void
  onOpenChange?: (open: boolean) => void
  onRetry?: () => void
  /** Remote readers have already applied the canonical matching policy. */
  filterOptionsLocally?: boolean
  loading?: boolean
  errorText?: string
  id?: string
  showUnhighlightedWithoutSearch?: boolean
}

export function Autocomplete({
  options,
  value,
  selectedLabel,
  onValueChange,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  notFoundText = "No results found.",
  className,
  disabled,
  onSearchChange,
  onOpenChange,
  onRetry,
  filterOptionsLocally = true,
  loading = false,
  errorText,
  id,
  showUnhighlightedWithoutSearch = true,
}: AutocompleteProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1)
  
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    if (!disabled || !open) return
    changeOpen(false)
  }, [disabled, open])

  const changeOpen = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearch("")
      setHighlightedIndex(-1)
      onSearchChange?.("")
    }
    onOpenChange?.(nextOpen)
  }

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!search.trim()) {
      return showUnhighlightedWithoutSearch
        ? options
        : options.filter((option) => option.groupHeader || option.highlighted)
    }
    if (!filterOptionsLocally) return options
    return options.filter((option) => smartTextMatches(`${option.label} ${option.value}`, search))
  }, [options, search, showUnhighlightedWithoutSearch, filterOptionsLocally])

  // Get selected option
  const selectedOption = options.find((option) => option.value === value)

  // Handle search change with external callback
  const handleSearchChange = (newSearch: string) => {
    if (disabled) return
    setSearch(newSearch)
    setHighlightedIndex(-1)
    onSearchChange?.(newSearch)
  }

  // Handle option selection
  const handleSelect = (optionValue: string) => {
    if (disabled) return
    onValueChange(optionValue)
    changeOpen(false)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (!open) {
      if (e.key === "Enter" || e.key === "ArrowDown") {
        e.preventDefault()
        changeOpen(true)
        return
      }
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setHighlightedIndex((prev) => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        )
        break
      case "ArrowUp":
        e.preventDefault()
        setHighlightedIndex((prev) => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        )
        break
      case "Enter":
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value)
        }
        break
      case "Escape":
        changeOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }

  // Scroll highlighted option into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.querySelectorAll<HTMLElement>('[role="option"]')[highlightedIndex]
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
        })
      }
    }
  }, [highlightedIndex])

  // Focus input when opened
  React.useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  return (
    <Popover open={open && !disabled} onOpenChange={(nextOpen) => { if (!disabled || !nextOpen) changeOpen(nextOpen) }}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border min-h-[44px] md:min-h-0 h-11 md:h-10 px-4 py-2 min-w-0 w-full justify-between bg-slate-50 border-slate-200 hover:bg-slate-100 text-[#2b3244]"
          disabled={disabled}
        >
          <span className="truncate">
            {selectedOption?.label || selectedLabel || placeholder}
          </span>
          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="flex min-w-0 max-w-[calc(100vw-16px)] flex-col overflow-hidden w-[var(--radix-popover-trigger-width)] p-0 bg-white border-slate-200 shadow-xl z-50"
        style={{ maxHeight: "min(360px, var(--radix-popover-content-available-height))" }}
        align="start" collisionPadding={8} sticky="always"
      >
        <div className="shrink-0 border-b bg-white border-slate-200">
          <Input
            ref={inputRef}
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={search}
            disabled={disabled}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[44px] text-[16px] md:text-sm border-0 bg-white focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div
          ref={listRef}
          className="min-h-0 max-h-[300px] overflow-y-auto overscroll-contain bg-white"
          role="listbox"
        >
          {loading && <div className="px-3 py-2 text-sm text-muted-foreground bg-white" role="status">Ricerca in corso…</div>}
          {errorText ? (
            <div className="px-3 py-2 text-sm text-destructive bg-white" role="alert">
              {errorText}
              {onRetry && <Button type="button" variant="outline" className="mt-2 min-h-[44px] w-full" onClick={onRetry}>Riprova ricerca</Button>}
            </div>
          ) : filteredOptions.length === 0 ? (
            !loading && <div className="px-3 py-2 text-sm text-muted-foreground bg-white">{notFoundText}</div>
          ) : (
            filteredOptions.map((option, index) => (
              <React.Fragment key={option.value}>
                {option.groupHeader && option.group && (
                  <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted border-t border-border">
                    {option.group}
                  </div>
                )}
                <div
                  className={cn(
                    "relative flex min-h-[44px] cursor-pointer select-none items-center px-3 py-2 text-sm outline-none bg-white",
                    "hover:bg-slate-100 focus:bg-slate-100",
                    highlightedIndex === index && "bg-slate-100",
                    value === option.value && "bg-slate-100",
                    option.highlighted && "bg-primary/10"
                  )}
                  onClick={() => { if (!disabled) handleSelect(option.value) }}
                  onMouseEnter={() => { if (!disabled) setHighlightedIndex(index) }}
                  role="option"
                  aria-selected={value === option.value}
                  aria-disabled={disabled}
                >
                  {option.icon && (
                    <span className="mr-2 flex h-4 w-4 items-center justify-center">
                      {option.icon}
                    </span>
                  )}
                  <span className="flex-1 truncate">{option.label}</span>
                  {value === option.value && (
                    <Check className="ml-2 h-4 w-4 opacity-100" />
                  )}
                </div>
              </React.Fragment>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
